
import axios from 'axios';
import { API_BASE_URL, ASSEET_LISTING_LIMIT } from '../constants/routes';
import { toast } from 'sonner';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getFilenameFromDisposition = (contentDisposition, fallback = 'download') => {
  if (!contentDisposition) {
    return fallback;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (asciiMatch?.[1]) {
    return asciiMatch[1];
  }

  return fallback;
};

const triggerBrowserDownload = (blob, filename) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(objectUrl);
};
// Add a request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('token');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {

    if (error.status === 401 && error.response?.data?.session_expiry) {
      toast.warning("Session Expired")
      localStorage.removeItem('id')
      localStorage.removeItem('token')
      window.location.href = "/login"
    }

    return Promise.reject(error);
  }
);


export const loginUser = async (username, password) => {
  try {
    const response = await apiClient.post(`login`, { username, password });
    return {

      data: response.data

    }
  } catch (error) {
    console.error(error);
    return {
      error
    }
  }
};

export const rolesType = async (userId) => {
  try {
    const response = await apiClient.get(`getRoleType/${userId}`);
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
};

export const userDetails = async (userId) => {
  try {
    const response = await apiClient.get(`user/details/${userId}`);
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
}
export const fetchImage = async (src, defaultImage) => {
  try {
    const response = await apiClient.get(src, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
  } catch (error) {
    return defaultImage;
  }
};


export const deactivatePreviousResetLinks = async (username) => {
  try {

    const response = await apiClient.put(`deactivatelink`, { username });

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error('Error deactivating previous reset links:', error);
    return false;
  }
};

export const checkResetLinkValidity = async (resetkey) => {
  try {
    const response = await apiClient.get(`checkresetlink/${resetkey}`);

    if (response.status === 200) {
      return true;
    }
  } catch (error) {
    if (error.response && error.response.status === 400) {
      console.error('Link Inactive: Bad Request');
      return false;
    } else {
      console.error('Link Inactive:', error);
      return false;
    }
  }
};

export const resetPassword = async (username) => {
  try {

    const deactivationResult = await deactivatePreviousResetLinks(username);

    if (deactivationResult) {
      const response = await apiClient.post(`user/forget/password`, { username });
      if (response.status === 200) {
        const responseData = response.data;
        if (responseData.success) {
          const { expiryDate } = responseData;
          return { success: true, expiryDate };
        } else {
          return { success: false };
        }
      } else {
        return { success: false };
      }
    } else {
      return { success: false };
    }
  } catch (error) {
    console.error('Error resetting password:', error);
    return { success: false };
  }
};
export const setNewPassw = async (resetkey, password, newPassword) => {
  try {
    const response = await apiClient.put(`contact-management/user/password/change/${resetkey}`, { password, newPassword });
    if (response.status === 200) {
      return { success: true };
    } else {
      return { success: false };
    }
  } catch (error) {
    console.error('Error addings emails:', error);
    return { success: false };
  }
};

export const logout = async (userId, refreshToken) => {
  try {
    const response = await apiClient.put(`logout/${userId}`, {
      refreshToken,
    });


    if (response.data.code === 200) {
      return response;
    } else {
      console.error('Logout failed:', response.data.message);
    }
  } catch (error) {
    console.error('Error logging out:', error);
  }
}

export const saveEmail = async (userId, primaryemail, primaryemailtype, secondaryemail, secondaryemailtype) => {
  try {
    const emailsData = {
      emails: [
        {
          email: primaryemail,
          emailTypes: primaryemailtype
        }
      ]
    };
    if (secondaryemail) {
      emailsData.emails.push({
        email: secondaryemail,
        emailTypes: secondaryemailtype
      });
    }

    const response = await apiClient.post(`user/email/${userId}`, emailsData);

    if (response.status === 200) {

      return { success: true };
    } else {

      return { success: false };
    }
  } catch (error) {
    console.error('Error adding emails', error);
    return { success: false };
  }

};


export const savePhone = async (userId, phoneNumbers) => {
  try {
    const validPhoneNumbers = phoneNumbers.filter(phoneDetail => phoneDetail.primaryNumber !== "");
    const phonesData = {
      phone: validPhoneNumbers.map(phoneNumber => ({
        phonenumbers: phoneNumber.primaryNumber
      }))
    };
    const response = await apiClient.post(`user/phone/${userId}`, phonesData);
    if (response.status === 200) {
      return { success: true };
    } else {
      return { success: false };
    }
  } catch (error) {
    console.error('Error adding phone numbers', error);
    return { success: false };
  }
};
export const getEmailPhone = async (userId) => {
  try {
    const response = await apiClient.get(`emailphone/${userId}`);
    if (response.status === 200) {
      return response.data[0];
    } else {
      return { success: false };
    }
  } catch (error) {
    console.error('Error getting contact details:', error);
    return { success: false };
  }

}

export const saveData = async (data, userId) => {
  try {
    const response = await apiClient.post(`userprofiledetails/${userId}`, data);
    return {
      data: response.data
    }
  } catch (error) {
    console.error(error);
    return {
      error
    }
  }
};


export const saveSocialAccountsData = async (data, userId) => {
  try {
    const response = await apiClient.post(`social/${userId}`, data);
    return {
      data: response.data
    }
  } catch (error) {
    console.error(error);
    return {
      error
    }
  }
};

export const saveProfilePhoto = async (data, userId) => {
  try {
    const response = await apiClient.post(`profile/photo/${userId}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return {
      data: response.data
    }
  } catch (error) {
    console.error(error);
    return {
      error
    }
  }
};


export const fetchProfilePhoto = async (userId) => {
  try {
    const response = await apiClient.get(`profile/photo/${userId}`);
    if (response.data.file_url) {
      return {
        data: `${response.data.file_url}`
      }
    } else {
      throw new Error('Profile photo not found');
    }
  } catch (error) {
    console.error(error);
    return {
      error
    }
  }
};

export const getUserProfileDetails = async (userId) => {
  try {
    const response = await apiClient.get(`user/profile_details/${userId}`);
    return response.data;

  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const saveDegrees = async (userId, activeDegrees) => {
  try {
    const educationData = {
      educations: activeDegrees.map((activeDegrees) => ({
        education_level: activeDegrees.education_level,
        university: activeDegrees.university,
        course: activeDegrees.course,
        specialization: activeDegrees.specialization,
        course_type: activeDegrees.course_type,
        start_year: activeDegrees.start_year,
        end_year: activeDegrees.end_year,
        medium: activeDegrees.medium,
        grade: activeDegrees.grade,
      })),
    };
    const response = await apiClient.post(
      `user/education/${userId}`,
      educationData
    );

    if (response.status === 200) {
      return { success: true, message: "Education Details successfully added" };
    } else {
      return {
        success: false,
        message: "Error occurred while adding Education Details",
      };
    }
  } catch (error) {
    console.error("Error adding Education details", error);
    return {
      success: false,
      message: "Error occurred while adding Education Details",
    };
  }
};

export const getCourses = async () => {
  try {
    const response = await apiClient.get(`user/courses`);
    return response.data;
  } catch (error) {
    console.error('Error fetching courses:', error);
    throw error; // Rethrow the error to handle it in the component
  }
};


export const getMedium = async () => {
  try {
    const response = await apiClient.get(`user/medium`);
    return response.data;
  } catch (error) {
    console.error('Error fetching courses:', error);
    throw error; // Rethrow the error to handle it in the component
  }
};

export const saveCertifications = async (userId, certifications) => {
  try {
    const certificationData = {
      certifications: certifications.map((certifications) => ({
        name: certifications.name,
        organization: certifications.organization,
        comp_id: certifications.comp_id,
        url: certifications.url,
        start_month: certifications.start_month,
        start_year: certifications.start_year,
        end_month: certifications.end_month,
        end_year: certifications.end_year,
      })),
    };
    const response = await apiClient.post(
      `user/certification/${userId}`, certificationData
    );

    if (response.status === 200) {
      return { success: true, message: "Certifications successfully added" };
    } else {
      return {
        success: false,
        message: "Error occurred while adding Certifications",
      };
    }
  } catch (error) {
    return {
      success: false,
      message: "Error occurred while adding Certifications",
    };
  }
};
export const userCreate = async (userData) => {
  try {
    const response = await apiClient.post(`createUser`, userData);
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
};

export const fetchProjects = async (userId) => {
  try {
    const response = await apiClient.get(`getProjects/${userId}`);
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
};

export const uploadLogoImage = async (file) => {
  try {
    const response = await apiClient.post(`logo/upload`, file, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
};


export const fetchLogo = async () => {
  try {
    const response = await apiClient.get(`fetch/logo`);
    if (response.data.file_url) {
      return {
        data: `${response.data.file_url}`
      }
    } else {
      throw new Error('Profile photo not found');
    }
  } catch (error) {
    // console.error(error);
    return {
      error
    }
  }
};

export const getUserDetails = async () => {
  try {
    const response = await apiClient.get(`get/userlist`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export const adminSetUserPassword = async (userId, password) => {
  try {
    const response = await apiClient.put(`admin/set/userpassword`, { userId, password });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
};

export const getUserProfessionDetails = async (userId) => {
  try {
    const response = await apiClient.get(`user/profession/${userId}`);
    return response.data;

  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const saveProfessionDetails = async (data, userId) => {
  try {
    const response = await apiClient.post(`userprofessiondetails/${userId}`, data);
    return {
      data: response.data
    }
  } catch (error) {
    console.error(error);
    return {
      error
    }
  }
};

export const getUserStatusDetails = async (userId) => {
  try {
    const response = await apiClient.get(`get/userStatuslist`);
    return response.data;

  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const saveStatusDetails = async (data) => {
  try {
    const response = await apiClient.put(`updateUserStatus`, data);
    return {
      data: response.data
    }
  } catch (error) {
    console.error(error);
    return {
      error
    }
  }
};

export const uploadUserProjectDetails = async (projects) => {
  try {
    const projectData = {
      projects: projects.map(project => ({
        project_name: project.projectName,
        project_royalty: project.projectRoyalty,
        project_startdate: project.startDate,
        project_enddate: project.endDate,
      })),
    };

    const response = await apiClient.post(`userProject/`, projectData);

    if (response.status === 200) {
      return { success: true };
    } else {
      return { success: false };
    }
  } catch (error) {
    console.error('Error adding project details', error);
    return { success: false };
  }
};

export const assignProjectToUsers = async (projectId, userIds) => {
  try {
    const response = await apiClient.post(`userProjectMapping/`, {
      projects: userIds.map(userId => ({
        project_id: projectId,
        user_id: userId
      }))
    });

    if (response.status === 200) {
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    console.error(error);
  }
};

export const fetchProject = async () => {
  const response = await apiClient.get(`get/projectlist`);
  return response.data.data; // Access the 'data' property of the response
};

export const fetchUsers = async () => {
  const response = await apiClient.get(`get/userlist`);
  if (response.data.code === 200) {
    return response.data.data.map(user => ({
      ...user,
      name: `${user.fname ?? ""} ${user.lname ?? ""}`.trim()
    }));
  } else {
    return [];
  }
};

export const saveThemes = async (theme1, theme2) => {
  const themeData = {
    theme1,
    theme2
  };
  try {
    const response = await apiClient.post(`admin/update/theme`, themeData);
    return { data: response.data };
  } catch (error) {
    console.error(error);
  }
};

export const fetchThemes = async () => {
  try {
    const response = await apiClient.get(`fetch/theme`);
    if (response.status !== 200) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const themeData = response.data;
    return themeData;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deletePhone = async (userId, PhoneNumber) => {
  try {
    const response = await apiClient.delete(`removephonenumbers/${userId}`, { data: { PhoneNumber } });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
};

export const deleteSocialAccounts = async (userId, url) => {
  try {
    const response = await apiClient.delete(`removesocialaccounts/${userId}`, { data: { url } });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
};

export const getAssetSearch = async (page, query, category, sort, tags) => {
  try {
    const response = await apiClient.get(`assetsearch`, {

      params: {
        page: page,
        limit: ASSEET_LISTING_LIMIT, // The constant value for the limit
        q: query,
        category: category,
        sort: sort,
        tags: Array.isArray(tags) ? tags.join(',') : (tags || undefined)
      }
    });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
}

export const getTags = async (q = '', all = false) => {
  try {
    const response = await apiClient.get(`tags`, { params: { q, all: all ? 1 : undefined } });
    return response?.data;
  }
  catch (error) {
    return {
      error
    }
  }
};

export const createTag = async (name) => {
  try {
    const response = await apiClient.post(`tags`, { name });
    return { data: response.data };
  }
  catch (error) {
    return {
      data: error?.response?.data
    }
  }
};

export const updateTagStatus = async (id, status) => {
  try {
    const response = await apiClient.patch(`tags/${id}`, { status });
    return { data: response.data };
  }
  catch (error) {
    return {
      data: error?.response?.data
    }
  }
};

export const downloadFile = (id, param, fileName) => {
  if (id) {
    return apiClient
      .post(`file/download/${id}`, param, { responseType: 'blob' })
      .then((response) => {
        const filename = getFilenameFromDisposition(
          response.headers['content-disposition'],
          fileName || `asset-${id}`
        );

        const contentType = response.headers['content-type'] || 'application/octet-stream';
        const blob = new Blob([response.data], { type: contentType });

        triggerBrowserDownload(blob, filename);

        return {
          data: {
            success: true,
            filename,
          },
        };
      });
  }
};

export const getAssetReviewHistory = async (assetId, page = 1, limit = 10) => {
  try {
    const response = await apiClient.get(`asset-review/${assetId}/history`, { params: { page, limit } });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const postAssetReviewComment = async (assetId, comment) => {
  try {
    const response = await apiClient.post(`asset-review/${assetId}/comment`, { comment });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const getInlineComments = async (assetId) => {
  try {
    const response = await apiClient.get(`asset-inline-comment/${assetId}`);
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const postInlineComment = async (assetId, { selectedText, anchor, comment }) => {
  try {
    const response = await apiClient.post(`asset-inline-comment/${assetId}`, {
      selected_text: selectedText,
      anchor,
      comment,
    });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const replyInlineComment = async (assetId, commentId, comment) => {
  try {
    const response = await apiClient.post(`asset-inline-comment/${assetId}/${commentId}/reply`, { comment });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const editInlineComment = async (assetId, commentId, comment) => {
  try {
    const response = await apiClient.patch(`asset-inline-comment/${assetId}/${commentId}`, { comment });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const deleteInlineComment = async (assetId, commentId) => {
  try {
    const response = await apiClient.delete(`asset-inline-comment/${assetId}/${commentId}`);
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const getInlineCommentNotifications = async (assetId) => {
  try {
    const response = await apiClient.get(`asset-inline-comment/${assetId}/notifications`);
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const markInlineCommentNotificationsAsRead = async (assetId) => {
  try {
    const response = await apiClient.post(`asset-inline-comment/${assetId}/notifications/read-all`);
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const getUserNotifications = async (page = 1, limit = 10) => {
  try {
    const response = await apiClient.get('notifications', { params: { page, limit } });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const getUnreadNotificationCount = async () => {
  try {
    const response = await apiClient.get('notifications/unread-count');
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await apiClient.post(`notifications/${notificationId}/read`);
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const downloadAssetReviewFile = (assetId, reviewId, fileName) => {
  return apiClient
    .get(`asset-review/${assetId}/download/${reviewId}`, { responseType: 'blob' })
    .then((response) => {
      const filename = getFilenameFromDisposition(
        response.headers['content-disposition'],
        fileName || `asset-${assetId}-review-${reviewId}`
      );

      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });

      triggerBrowserDownload(blob, filename);

      return { data: { success: true, filename } };
    })
    .catch((error) => ({ error }));
};

export const assetDetail = async (id, params) => {
  try {
    const response = await apiClient
      .get(`assetsdetails/${id}`, { params })
    return response?.data;

  }
  catch (error) {
    throw new Error(error);

  }

};


export const assetCategory = async () => {
  try {
    const response = await apiClient
      .get(`asset-category`)
    return response?.data;

  }
  catch (error) {
    return {
      error
    }
  }

};

export const getReviewers = async (assetId) => {
  try {
    const response = await apiClient
      .get(`reviewers`, { params: assetId ? { asset_id: assetId } : {} })
    return response?.data;

  }
  catch (error) {
    return {
      error
    }
  }

};

export const forwardAssetReview = async (assetId, reviewerIds) => {
  try {
    const response = await apiClient.post(`asset-review/${assetId}/forward`, { reviewer_ids: reviewerIds });
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const getAssetReviewers = async (assetId) => {
  try {
    const response = await apiClient.get(`asset-review/${assetId}/reviewers`);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const fileZipUpload = async (formData) => {
  try {
    const response = await apiClient.post(`upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
};

export const addAsset = async (data, id = 0) => {
  try {
    let response;
    const numericId = Number(id);
    const isCreate = !id || Number.isNaN(numericId) || numericId <= 0;

    if (isCreate)
      response = await apiClient.post(`saveasset`, data);
    else
      response = await apiClient.post(`edit-asset/${numericId}`, data);


    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};


export const register = async (data) => {
  try {
    const response = await apiClient.post(`user/register`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const user_create = async (data) => {
  try {
    const response = await apiClient.post(`user/create`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const emailverification = async (data, id) => {
  try {
    const response = await apiClient.post(`email/verification/${id}`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const resendEmailCode = async (id) => {
  try {
    const response = await apiClient.post(`email/resend/${id}`);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};


export const getBusinessUnits = async (id) => {
  try {
    const endpoint = typeof id === 'undefined' || id === null || id === ''
      ? 'business-unit'
      : `business-unit/${id}`;
    const response = await apiClient.get(endpoint);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const getDepartments = async (id) => {
  try {
    const response = await apiClient.get(`departments/${id}`);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const getBusinessGroups = async () => {
  try {
    const response = await apiClient.get(`business-groups`);
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
}

export const getUsers = async (params) => {
  try {
    const response = await apiClient.get(`users`, { params });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
}


export const user_status = async (data) => {
  try {
    const response = await apiClient.post(`user_status_update`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};


export const getUserData = async (user_id) => {
  try {
    const response = await apiClient.get(`user/${user_id}`);
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
}

export const getUserOrgDetails = async (user_id) => {
  try {
    const response = await apiClient.get(`user/${user_id}/org-details`);
    return { data: response.data };
  } catch (error) {
    return { error };
  }
}

export const user_edit = async (id, data) => {
  try {
    const response = await apiClient.post(`user/edit/${id}`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};


export const getAssets = async (params) => {
  try {
    const response = await apiClient.get(`manage-asset`, { params });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
}

export const asset_status = async (data) => {
  try {
    const response = await apiClient.post(`asset_status_update`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const updateAssetPublishedStatus = async (data) => {
  try {
    const response = await apiClient.post(`asset-published-status`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};


export const assetReport = async (params) => {
  try {
    const response = await apiClient.get(`asset-reports`, { params });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
}


export const asset_suggestion = async (data) => {
  try {
    const response = await apiClient.post(`assetSuggestions`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};


export const get_myaccount = async (params = null) => {
  try {
    const response = await apiClient.get(`myaccount`, { params });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
}

export const update_account = async (data) => {
  try {
    const response = await apiClient.post(`update-account`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const get_email_notification_settings = async () => {
  try {
    const response = await apiClient.get(`email-notification-settings`);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const update_email_notification_settings = async (data) => {
  try {
    const response = await apiClient.put(`email-notification-settings`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const change_password = async (data) => {
  try {
    const response = await apiClient.post(`change-password`, data);
    return { data: response.data };
  } catch (error) {
    return {
      error
    }
  }
};

export const forgot_password = async (data) => {
  try {
    const response = await apiClient.post(`user/forgot-password`, data);
    return {
      data: response.data
    }
  } catch (error) {
    console.error(error);
    return {
      error
    }
  }
};

export const set_new_password = async (token, data) => {
  try {
    const response = await apiClient.post(`password-update/${token}`, data);
    return {
      data: response.data
    }
  } catch (error) {
    console.error(error);
    return {
      error
    }
  }
};

export const trending_category = async () => {
  try {
    const response = await apiClient
      .get(`trending-category`)
    return response?.data;

  }
  catch (error) {
    return {
      error
    }
  }

};


export const get_dashboard_data = async (params) => {
  try {
    const response = await apiClient.get(`analytical-report/all`, { params });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
}

export const getAllowedDomains = async () => {
  try {
    const response = await apiClient.get(`allowed-domains`);
    return { data: response};
  }
  catch (error) {
    return {
      error
    }
  }
};

export const get_recent_assets = async (params) => {
  try {
    const response = await apiClient.get(`analytical-report/recentlyAddedAssets`, { params });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const fileCSVUpload = async (formData) => {
  try {
    const response = await apiClient.post(`csvupload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    return { data: response.data };
  }
  catch (error) {
    return {
      error
    }
  }
};

export const validateToken = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const response = await apiClient.get(`validate-token`,
      {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.valid;
  } catch (error) {
       return false;
  }
};

export const getBuHeads = async (params) => {
  try {
    const response = await apiClient.get(`business-unit-heads`, { params });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const getAvailableBuUnits = async (excludeId = null) => {
  try {
    const params = excludeId !== null ? { exclude_id: excludeId } : {};
    const response = await apiClient.get(`business-unit-heads/available-units`, { params });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const getBuHeadDetails = async (id) => {
  try {
    const response = await apiClient.get(`business-unit-head/${id}`);
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const createBuHead = async (data) => {
  try {
    const response = await apiClient.post(`business-unit-head/create`, data);
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const updateBuHead = async (id, data) => {
  try {
    const response = await apiClient.post(`business-unit-head/edit/${id}`, data);
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const get_org_hierarchy = async () => {
  try {
    const { data: accountData, error: accountError } = await get_myaccount();
    if (accountError || !accountData?.data) return { error: accountError || 'Account data unavailable' };

    const user = accountData.data;
    const buId = user.business_unit_id;
    const bgId = user.bgid;
    const deptId = user.department_id;

    const hierarchy = {
      user: {
        fname: user.fname,
        lname: user.lname,
        role_type: user.role_name || (user.role_type === 1 ? 'Admin' : user.role_type === 3 ? 'Contributor' : 'User'),
      },
    };

    if (bgId || buId) {
      const { data: buData } = await getBusinessUnits(bgId);
      const bus = Array.isArray(buData?.data) ? buData.data : buData?.data ? [buData.data] : [];
      if (bus.length > 0 && bus[0].bg_name) hierarchy.business_group = bus[0].bg_name;
      const userBu = bus.find(b => String(b.id) === String(buId));
      if (userBu) hierarchy.business_unit = userBu.name;
    }

    if (buId) {
      const { data: headData } = await getBuHeads({ bu: buId });
      const heads = Array.isArray(headData?.data) ? headData.data : [];
      if (heads.length > 0) hierarchy.bu_head = heads[0].name;
    }

    if (buId && deptId) {
      const { data: deptData } = await getDepartments(buId);
      const depts = Array.isArray(deptData?.data) ? deptData.data : deptData?.data ? [deptData.data] : [];
      const userDept = depts.find(d => String(d.id) === String(deptId));
      if (userDept) hierarchy.user_business_unit = userDept.dept_name || userDept.name;
    } else if (hierarchy.business_unit) {
      hierarchy.user_business_unit = hierarchy.business_unit;
    }

    return { data: { data: hierarchy } };
  } catch (error) {
    return { error };
  }
};